import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount } from "wagmi";
import { api } from "../../lib/api";
import { normalizeOwnerIdentity } from "../../domain/territory";
import {
  clearCsrfToken,
  reconcileTerritoryIdentity,
  resolveTerritory,
  saveTerritoryIdentity,
  TerritoryIdentityError,
} from "../../lib/territory-identity-api";
import { emitTerritoryIdentityMutation } from "../../lib/territory-identity-invalidation";
import type {
  ReconciliationCandidate,
  ResolvedTerritory,
  TerritoryIdentityRecord,
  TerritoryResolution,
} from "../../domain/territory-identity";

/**
 * WO#4B — authenticated Territory Identity management state.
 *
 * Auth boundary (WO#3 law preserved): a connected wallet is NOT sufficient — a
 * valid SIWE session (`api.me()`) matching the connected wallet is required.
 * Async safety: monotonic request id + AbortController; a stale wallet-A result
 * can never populate under wallet B; disconnect/mismatch/expiry fail closed.
 * No optimistic persisted state: the view reflects only backend-confirmed data.
 */
export type IdentityState =
  | { status: "auth_required" }
  | { status: "unavailable" } // territory below the 400 u² publishability law
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "reconciliation_required";
      territory: ResolvedTerritory;
      candidates: ReconciliationCandidate[];
    }
  | {
      status: "ready";
      territory: ResolvedTerritory;
      identity: TerritoryIdentityRecord | null;
      resolution: TerritoryResolution;
    };

export interface SaveError {
  code: string;
  message: string;
}

export interface UseTerritoryIdentity {
  state: IdentityState;
  saving: boolean;
  saveError: SaveError | null;
  save: (displayName: string) => Promise<boolean>;
  reconciling: boolean;
  reconcileError: SaveError | null;
  reconcile: (survivorIdentityId: string) => Promise<boolean>;
  reload: () => void;
}

export function useTerritoryIdentity(
  seedParcelId: number | null,
  identityEligible: boolean,
): UseTerritoryIdentity {
  const { address, isConnected } = useAccount();
  const [state, setState] = useState<IdentityState>({ status: "auth_required" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileError, setReconcileError] = useState<SaveError | null>(null);
  const requestIdRef = useRef(0);
  const latestRef = useRef<IdentityState>(state);
  latestRef.current = state;
  const [reloadTick, setReloadTick] = useState(0);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  // Clear a prior save error only when the territory/wallet CONTEXT changes —
  // never on a reload() re-resolve, so a STALE/CONFLICT message stays visible
  // while the authoritative state refreshes underneath it. The session-bound
  // CSRF token is also dropped here so a wallet switch / disconnect can never
  // reuse wallet A's token under wallet B.
  useEffect(() => {
    setSaveError(null);
    setReconcileError(null);
    clearCsrfToken();
  }, [address, isConnected, seedParcelId, identityEligible]);

  useEffect(() => {
    const myId = ++requestIdRef.current;
    const controller = new AbortController();

    if (!isConnected || !address || seedParcelId === null) {
      setState({ status: "auth_required" });
      return () => controller.abort();
    }
    if (!identityEligible) {
      setState({ status: "unavailable" });
      return () => controller.abort();
    }

    setState({ status: "loading" });
    (async () => {
      // 1. Prove authenticated SIWE session matches the connected wallet.
      try {
        const me = await api.me();
        if (normalizeOwnerIdentity(me.wallet) !== normalizeOwnerIdentity(address)) {
          if (myId === requestIdRef.current) setState({ status: "auth_required" });
          return;
        }
      } catch {
        if (myId === requestIdRef.current) setState({ status: "auth_required" });
        return;
      }
      // 2. Authoritative territory resolution by seed parcel locator.
      try {
        const r = await resolveTerritory(seedParcelId, controller.signal);
        if (myId !== requestIdRef.current) return;
        if (r.resolution === "reconciliation_required") {
          setState({
            status: "reconciliation_required",
            territory: r.territory,
            candidates: r.reconciliation ? r.reconciliation.candidates : [],
          });
        } else {
          setState({
            status: "ready",
            territory: r.territory,
            identity: r.identity,
            resolution: r.resolution,
          });
        }
      } catch (e) {
        if (controller.signal.aborted || myId !== requestIdRef.current) return;
        if (e instanceof TerritoryIdentityError && e.status === 401) {
          setState({ status: "auth_required" });
        } else {
          setState({ status: "error", message: "Territory could not be resolved." });
        }
      }
    })();

    return () => controller.abort();
  }, [address, isConnected, seedParcelId, identityEligible, reloadTick]);

  const save = useCallback(
    async (displayName: string): Promise<boolean> => {
      const current = latestRef.current;
      if (current.status !== "ready" || seedParcelId === null) return false;
      setSaving(true);
      setSaveError(null);
      try {
        const res = await saveTerritoryIdentity({
          seedParcelId,
          displayName,
          expectedMembershipFingerprint: current.territory.membership_fingerprint,
          expectedRevision: current.identity ? current.identity.revision : null,
        });
        setState({
          status: "ready",
          territory: res.territory,
          identity: res.identity,
          resolution: res.resolution,
        });
        // Notify same-page consumers (My Territories list + detail header) to
        // re-resolve the name/thumbnail so the slots update immediately.
        emitTerritoryIdentityMutation();
        return true;
      } catch (e) {
        if (e instanceof TerritoryIdentityError) {
          setSaveError({ code: e.code, message: e.message });
          // Ownership/territory or lineage changed → re-resolve authoritatively.
          if (
            e.code === "STALE_TERRITORY" ||
            e.code === "IDENTITY_VERSION_CONFLICT" ||
            e.code === "IDENTITY_RECONCILIATION_REQUIRED"
          ) {
            reload();
          }
        } else {
          setSaveError({ code: "UNKNOWN", message: "Save failed. Please try again." });
        }
        return false;
      } finally {
        setSaving(false);
      }
    },
    [seedParcelId, reload],
  );

  const reconcile = useCallback(
    async (survivorIdentityId: string): Promise<boolean> => {
      const current = latestRef.current;
      if (current.status !== "reconciliation_required" || seedParcelId === null) return false;
      // Explicit selection only: the survivor must be one of the shown candidates.
      if (!current.candidates.some((c) => c.identity_id === survivorIdentityId)) return false;
      // Bind this mutation to the resolve generation it was shown against. A
      // wallet switch / disconnect / seed change / reload bumps requestIdRef, so
      // a stale reconcile response can never render as current authority.
      const myId = requestIdRef.current;
      setReconciling(true);
      setReconcileError(null);
      try {
        const res = await reconcileTerritoryIdentity({
          seedParcelId,
          survivorIdentityId,
          expectedMembershipFingerprint: current.territory.membership_fingerprint,
          expectedCandidates: current.candidates.map((c) => ({
            identityId: c.identity_id,
            expectedRevision: c.revision,
          })),
        });
        if (myId !== requestIdRef.current) return false; // wallet/session/context changed
        setState({
          status: "ready",
          territory: res.territory,
          identity: res.identity,
          resolution: res.resolution,
        });
        emitTerritoryIdentityMutation();
        return true;
      } catch (e) {
        if (myId !== requestIdRef.current) return false; // stale — suppress
        if (e instanceof TerritoryIdentityError) {
          setReconcileError({ code: e.code, message: e.message });
          // Any authority/concurrency change → re-resolve authoritatively.
          if (
            e.code === "STALE_TERRITORY" ||
            e.code === "IDENTITY_VERSION_CONFLICT" ||
            e.code === "RECONCILIATION_NOT_REQUIRED" ||
            e.code === "SURVIVOR_NOT_CANDIDATE"
          ) {
            reload();
          }
        } else {
          setReconcileError({ code: "UNKNOWN", message: "Reconciliation failed. Please try again." });
        }
        return false;
      } finally {
        setReconciling(false);
      }
    },
    [seedParcelId, reload],
  );

  return { state, saving, saveError, save, reconciling, reconcileError, reconcile, reload };
}
